"use client"

/**
 * NotificationBell — shell utility-bar trigger + floating sheet inbox.
 *
 * Backed by `lib/notifications-store` (exam mock feed plus live import
 * outcomes). The badge counts unseen items; opening the sheet clears unseen
 * without marking rows read. Settings live as a sub-panel in the same rail.
 */

import * as React from "react"
import { Link } from "react-router"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { FilterChipGroup } from "@/components/ui/filter-chip-group"
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field"
import { Tip } from "@/components/ui/tip"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import {
  NOTIFICATION_KIND_ICON,
  type NotificationItem,
  type NotificationKind,
} from "@/lib/mock/notifications"
import {
  setNotificationKindEnabled,
  useNotificationPreferences,
} from "@/lib/notification-preferences"
import {
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationsSeen,
  useNotifications,
} from "@/lib/notifications-store"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
  railTriggerProps,
} from "@/lib/floating-sheet-panel"

type InboxFilter = "all" | "unread"
type SheetPanel = "inbox" | "settings"

const KIND_SETTINGS: { kind: NotificationKind; label: string; id: string }[] = [
  { kind: "import", label: "Import jobs", id: "notify-kind-import" },
  { kind: "assessment", label: "Assessments", id: "notify-kind-assessment" },
  { kind: "submission", label: "Submissions", id: "notify-kind-submission" },
]

export function NotificationBell({ className }: { className?: string }) {
  const items = useNotifications()
  const prefs = useNotificationPreferences()
  const [open, setOpen] = React.useState(false)
  const [panel, setPanel] = React.useState<SheetPanel>("inbox")
  const [filter, setFilter] = React.useState<InboxFilter>("all")

  const feed = items.filter((item) => prefs.kinds[item.kind])
  const unreadCount = feed.filter((item) => !item.read).length
  const unseenCount = feed.filter((item) => !item.seen).length
  const visibleItems =
    filter === "unread" ? feed.filter((item) => !item.read) : feed

  const bellLabel =
    unseenCount > 0
      ? `Notifications, ${unseenCount} new`
      : unreadCount > 0
        ? `Notifications, ${unreadCount} unread`
        : "Notifications"

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (next) markNotificationsSeen()
    else {
      setFilter("all")
      setPanel("inbox")
    }
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={bellLabel}
            aria-expanded={open}
            className={cn("relative", className)}
            {...railTriggerProps()}
            onClick={() => handleOpenChange(!open)}
          >
            <i className="fa-light fa-bell text-sm" aria-hidden="true" />
            {unseenCount > 0 ? (
              <Badge
                variant="count"
                className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-2xs"
              >
                {unseenCount > 9 ? "9+" : unseenCount}
              </Badge>
            ) : null}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">Notifications</TooltipContent>
      </Tooltip>

      <FloatingSheetPanel open={open} onOpenChange={handleOpenChange}>
        <FloatingSheetPanelContent size="sm" contentSlot="notifications">
          <FloatingSheetPanelToolbar
            onBack={
              panel === "settings" ? () => setPanel("inbox") : undefined
            }
            backLabel="Notifications"
            actions={
              panel === "inbox" ? (
                <div className="flex items-center gap-1">
                  {unreadCount > 0 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => markAllNotificationsRead()}
                    >
                      Mark all as read
                    </Button>
                  ) : null}
                  <Tip label="Settings">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Notification settings"
                      onClick={() => setPanel("settings")}
                    >
                      <i className="fa-light fa-gear text-sm" aria-hidden="true" />
                    </Button>
                  </Tip>
                </div>
              ) : null
            }
          />
          <FloatingSheetPanelHeader
            title={panel === "settings" ? "Settings" : "Notifications"}
            subtitle={
              panel === "inbox" && unreadCount > 0
                ? `${unreadCount} unread`
                : undefined
            }
          />
          <FloatingSheetPanelBody className="gap-3 px-4 pb-4">
            {panel === "settings" ? (
              <NotificationSettingsPanel />
            ) : (
              <>
                <FilterChipGroup
                  value={filter}
                  onValueChange={setFilter}
                  options={[
                    { value: "all", label: "All", count: feed.length },
                    { value: "unread", label: "Unread", count: unreadCount },
                  ]}
                  variant="brand"
                  size="default"
                  aria-label="Notification filter"
                />

                {visibleItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 px-2 py-8 text-center">
                    <span
                      aria-hidden
                      className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground"
                    >
                      <i className="fa-light fa-bell" aria-hidden="true" />
                    </span>
                    <p className="text-sm font-medium text-foreground">
                      {filter === "unread"
                        ? "No unread notifications"
                        : "You're all caught up."}
                    </p>
                  </div>
                ) : (
                  <ul className="flex flex-col gap-0.5">
                    {visibleItems.map((item) => (
                      <li key={item.id}>
                        <NotificationRow
                          item={item}
                          onRead={() => {
                            markNotificationRead(item.id)
                            handleOpenChange(false)
                          }}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </FloatingSheetPanelBody>
        </FloatingSheetPanelContent>
      </FloatingSheetPanel>
    </>
  )
}

function NotificationSettingsPanel() {
  const prefs = useNotificationPreferences()

  return (
    <FieldGroup className="w-full gap-6">
      <FieldSet>
        <FieldLegend variant="label">Notify about</FieldLegend>
        <div className="flex flex-col gap-3">
          {KIND_SETTINGS.map((row) => (
            <Field
              key={row.kind}
              orientation="horizontal"
              className="w-full items-center justify-between gap-4"
            >
              <FieldLabel htmlFor={row.id}>{row.label}</FieldLabel>
              <ToggleSwitch
                id={row.id}
                checked={prefs.kinds[row.kind]}
                onChange={(enabled) =>
                  setNotificationKindEnabled(row.kind, enabled)
                }
              />
            </Field>
          ))}
        </div>
      </FieldSet>
    </FieldGroup>
  )
}

function NotificationRow({
  item,
  onRead,
}: {
  item: NotificationItem
  onRead: () => void
}) {
  const content = (
    <div
      className={cn(
        "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors",
        item.read
          ? "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          : "bg-[var(--status-badge-info-fill)]"
      )}
    >
      <i
        className={cn(
          "fa-light mt-0.5 shrink-0 text-muted-foreground",
          NOTIFICATION_KIND_ICON[item.kind]
        )}
        aria-hidden="true"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium text-foreground">{item.title}</span>
        <span className="text-xs text-muted-foreground">{item.description}</span>
        <span className="text-xs text-muted-foreground">{item.timestamp}</span>
      </span>
      {!item.read ? (
        <>
          <span className="sr-only">Unread</span>
          <span
            aria-hidden="true"
            className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[var(--status-badge-info-fg)]"
          />
        </>
      ) : null}
    </div>
  )

  if (item.href) {
    return (
      <Link to={item.href} onClick={onRead} className="block">
        {content}
      </Link>
    )
  }

  return (
    <button type="button" onClick={onRead} className="block w-full">
      {content}
    </button>
  )
}
